import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useArtistStore } from "../../stores/artistStore.js";
import { useBandStore } from "../../stores/bandStore.js";
import { SLOT_ROLES, type SlotRole } from "../../infra/api.js";
import { Button } from "../../ui/Button.js";
import { LpcAvatar } from "./LpcAvatar.js";
import { AvatarFrame } from "./AvatarFrame.js";

const ROLE_LABELS: Record<SlotRole, string> = {
  vocal: "Vocal",
  guitar: "Guitarra",
  bass: "Baixo",
  drums: "Bateria",
  keys: "Teclado",
};

export function BandNew(): JSX.Element {
  const artists = useArtistStore((s) => s.artists);
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [slots, setSlots] = useState<Record<SlotRole, string | undefined>>({
    vocal: undefined,
    guitar: undefined,
    bass: undefined,
    drums: undefined,
    keys: undefined,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void useArtistStore.getState().fetch();
  }, []);

  const filledRoles = SLOT_ROLES.filter((r) => slots[r]);

  async function onSave(): Promise<void> {
    if (!name.trim()) {
      setError("Nome obrigatório");
      return;
    }
    if (filledRoles.length === 0) {
      setError("Adicione ao menos um membro");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const band = await useBandStore.getState().create({
        name: name.trim(),
        slots: filledRoles.map((r) => ({ slot_role: r, artist_id: slots[r]! })),
      });
      nav(`/bands/${band.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <header className="reveal border-b border-border pb-6">
        <p className="catalog-num mb-2">FICHA / NOVA BANDA</p>
        <h1
          className="font-display text-[64px] leading-[0.88] tracking-tight text-paper"
          style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
        >
          Forme um <span className="italic text-vinyl">grupo</span>
        </h1>
      </header>

      {artists.length === 0 && (
        <div className="reveal-1 border border-butter/40 bg-butter/5 p-4 text-sm">
          <p className="font-mono text-xs uppercase tracking-widest2 text-butter">atenção</p>
          <p className="mt-1 text-ink">
            Você precisa de pelo menos um personagem antes.{" "}
            <Link
              to="/artists/new"
              className="underline decoration-vinyl underline-offset-4 hover:text-paper"
            >
              Cadastrar primeiro personagem →
            </Link>
          </p>
        </div>
      )}

      <div className="reveal-2 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <div className="border border-border bg-tape p-6">
            <p className="catalog-num mb-3">01 / NOME DO SELO</p>
            <input
              className="w-full border-0 border-b border-border bg-transparent px-0 py-3 font-display text-4xl text-paper outline-none placeholder:text-ink-dim focus:border-paper"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex: Lua Cheia"
              autoFocus
            />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4">
          <div className="h-full border border-border bg-tape p-5">
            <p className="catalog-num mb-2">RESUMO</p>
            <dl className="flex flex-col gap-1 font-mono text-[11px] uppercase tracking-widest2">
              <div className="flex justify-between border-b border-border py-1">
                <dt className="text-ink-dim">membros</dt>
                <dd className="tabular-nums text-paper">
                  {String(filledRoles.length).padStart(2, "0")} / 05
                </dd>
              </div>
              <div className="flex justify-between border-b border-border py-1">
                <dt className="text-ink-dim">elenco disponível</dt>
                <dd className="tabular-nums text-paper">
                  {String(artists.length).padStart(2, "0")}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <div className="reveal-3 border border-border bg-tape">
        <header className="border-b border-border px-5 py-3">
          <p className="catalog-num">02 / ELENCO POR PAPEL</p>
        </header>
        <div className="divide-y divide-border">
          {SLOT_ROLES.map((role) => {
            const selectedId = slots[role];
            const selected = artists.find((a) => a.id === selectedId);
            return (
              <div key={role} className="grid grid-cols-12 items-center gap-3 px-5 py-3">
                <div className="col-span-3">
                  <p
                    className="font-display text-xl text-paper"
                    style={{ fontVariationSettings: '"opsz" 36, "wght" 500' }}
                  >
                    {ROLE_LABELS[role]}
                  </p>
                  <p className="font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
                    .{role}
                  </p>
                </div>
                <div className="col-span-7">
                  <select
                    className="w-full border border-border bg-asphalt px-3 py-2 font-mono text-sm text-paper focus:border-paper focus:outline-none"
                    value={selectedId ?? ""}
                    onChange={(e) => setSlots({ ...slots, [role]: e.target.value || undefined })}
                  >
                    <option value="">— vago —</option>
                    {artists.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2 flex justify-end">
                  {selected ? (
                    <AvatarFrame size={48} tone="warm">
                      <LpcAvatar
                        layers={selected.lpc_layers}
                        size={48}
                        label={selected.name}
                        instrument={role}
                      />
                    </AvatarFrame>
                  ) : (
                    <div className="h-12 w-12 border border-dashed border-border" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <p className="border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl">
          {error}
        </p>
      )}

      <div className="reveal-4 flex justify-between">
        <Button variant="ghost" onClick={() => nav("/bands")}>
          ← cancelar
        </Button>
        <Button variant="primary" onClick={() => void onSave()} disabled={saving}>
          {saving ? "Lançando…" : "Lançar banda"}
        </Button>
      </div>
    </div>
  );
}
